import React from 'react';
import { AvatarIcon as Avatar, ExpandButton, Label } from '@lt/components';

import { formatDateToDayFull } from 'src/utils/dateUtils';

import ThumbsUp from '@lt/components/icons-react/rebrand/Like';
import { useTranslation } from '@lt/localization';
import type { TLabelStyle } from '@lt/components/components/Label/Label.model';
import type { ReviewsProps } from '@lt/api/dist/methods/hotelReviews/types';
import classnames from 'classnames';
import { letterDescriptionRating } from './utils/utils';
import AnswerToReview from './components/AnswerToReview/AnswerToReview';
import {
  TEST_ID_REVIEW_CARD_ANSWER,
  TEST_ID_REVIEW_CARD_CONTENT,
  TEST_ID_REVIEW_CARD_MINUS_CONTENT,
  TEST_ID_REVIEW_CARD_PLUS_CONTENT,
} from './constants';

import styles from './reviewsCard.module.css';
import GallerySwiperReviewCard from './components/GallerySwiperReviewCard';

export type ReviewsCardProps = ReviewsProps & {
  isMobile: boolean;
  className?: string;
};

const ReviewsCard = (props: ReviewsCardProps) => {
  const {
    content,
    name,
    createdAt,
    averageRating,
    avatarUrl,
    isMobile = false,
    answer = '',
    answeredAt = '',
    reviewPlus,
    reviewMinus,
    images,
    className = '',
    title,
    tags,
  } = props;

  const { t } = useTranslation();

  const reviewDate = formatDateToDayFull(createdAt);
  const rating = letterDescriptionRating(averageRating);

  return (
    <div
      className={classnames(styles.reviewItem, className, {
        [styles.reviewItemMobile]: isMobile,
      })}
    >
      <div className={styles.container}>
        <div
          className={styles.ratingReview}
          style={{ backgroundColor: rating.styleString }}
        >
          {rating.text}
        </div>
        <div className={styles.ratingDate}>{reviewDate}</div>
      </div>
      <div className={styles.container}>
        <div>
          <Avatar
            className={styles.imageComponent}
            avatarUrl={avatarUrl}
            size={40}
            name={name}
          />
        </div>
        <div className={styles.header}>
          <div className={styles.name}>{name}</div>
          <div className={styles.title}>{title}</div>
        </div>
      </div>
      {images.length > 0 && (
        <GallerySwiperReviewCard
          title={`ReviewCard-${name}`}
          images={images}
          isMobile={isMobile}
        />
      )}
      {reviewPlus && (
        <div
          className={styles.reviews}
          data-testid={TEST_ID_REVIEW_CARD_PLUS_CONTENT}
        >
          <Label
            className={styles.label}
            title={t('hotel.reviews.reviewPlus')}
            style={'PLUS' as TLabelStyle}
            iconComponent={
              <ThumbsUp
                className={classnames(styles.thumbs, styles.thumbsUp)}
              />
            }
          />
          <ExpandButton
            className={styles.expandButton}
            content={reviewPlus}
            visibleLine={isMobile ? 4 : 3}
            isInitialHiddenText={tags.length === 0}
          />
        </div>
      )}
      {reviewMinus && (
        <div
          className={styles.reviews}
          data-testid={TEST_ID_REVIEW_CARD_MINUS_CONTENT}
        >
          <Label
            className={styles.label}
            title={t('hotel.reviews.reviewMinus')}
            style={'MINUS' as TLabelStyle}
            iconComponent={
              <ThumbsUp
                className={classnames(styles.thumbs, styles.thumbsDown)}
              />
            }
          />
          <ExpandButton
            className={styles.expandButton}
            content={reviewMinus}
            visibleLine={isMobile ? 4 : 3}
            isInitialHiddenText={tags.length === 0}
          />
        </div>
      )}
      {!reviewPlus && !reviewMinus && (
        <div data-testid={TEST_ID_REVIEW_CARD_CONTENT}>
          <ExpandButton
            className={styles.expandButton}
            content={content ?? ''}
            visibleLine={isMobile ? 6 : 3}
            isInitialHiddenText={tags.length === 0}
          />
        </div>
      )}
      {answer && (
        <div data-testid={TEST_ID_REVIEW_CARD_ANSWER}>
          <AnswerToReview
            answer={answer}
            answeredAt={answeredAt}
            isMobile={isMobile}
          />
        </div>
      )}
    </div>
  );
};

export default ReviewsCard;
